import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface HeritageSingleSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const HeritageSingleSection: React.FC<HeritageSingleSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#heritage-single`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Heritage Single Rooms with charming village views and classic Alpine charm`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.heritageSingleBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.heritageSingleBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.heritageSingleBadges.bedding"),
      description: "Comfortable single bed",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.heritageSingleBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  return (
    <section
      id="heritage-single"
      className="py-16 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden"
      data-oid="kgjr045"
    >
      {/* Background Quadrilaterals - Mirrored positions */}
      <div
        className="absolute top-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="003-.r2"
      ></div>
      <div
        className="absolute bottom-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="kerbf_w"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="rj:9_fy"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="tmskhrp">
          <div className="mb-4" data-oid=":ddj4fl">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="l6nsob0"
            >
              {t("rooms.heritageSingle")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid=":nct60c"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="8_h-sv9"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="-3.8g:8"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="ptpal:b"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="1n22wuo"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="-e3h-re"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="pf:sbdk"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="2eqdiin"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="nd7b-f4"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="cliwbw-"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Content Left (40-50%), Carousel Right (50-60%) - MIRRORED */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[45%_55%] 2xl:grid-cols-[40%_60%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="ts_q.px"
        >
          {/* Left Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pl-4 lg:pl-6 xl:pl-8 order-2 lg:order-1"
            data-oid="fx9t_47"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid=".qu8ssd">
              <div className="mb-4" data-oid="ze:k7ju">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="ojrw09p"
                >
                  {t("rooms.heritageSingle")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="_gaaaym"
              >
                <div className="flex items-center gap-2" data-oid="keo_19m">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="4s90_2t"
                  />

                  <span className="text-sm text-gray-900" data-oid="g_ec27u">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="06.6947">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="006qugb"
                  />

                  <span className="text-sm text-gray-900" data-oid="sugff.0">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="i12krzf">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="okp5ywy"
                  />

                  <span className="text-sm text-gray-900" data-oid="vx7u3hx">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="zpx1a:m">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid=":2-v385"
              >
                <span className="sm:hidden" data-oid="q3iq:9i">
                  {t("rooms.heritageSingleDescription.mobile")}
                </span>
                <span className="hidden sm:inline" data-oid="8iyioz8">
                  {t("rooms.heritageSingleDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="tf4h6_m">
              {/* Charming village views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="fd.j4lc"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="ohgu4lb"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="g6-fqt4"
                >
                  {t("rooms.heritageSingleFeatures.villageViews")}
                </span>
              </div>

              {/* Classic Alpine charm */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="d2f2p4k"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="3nh4ai:"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="2vww_d3"
                >
                  {t("rooms.heritageSingleFeatures.classicCharm")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="gjwshky"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=":ue0.in"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="qwf5noj"
                >
                  Free Wifi
                </span>
              </div>

              {/* Flatscreen TV */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="wub_efm"
              >
                <Tv
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="d9m-vih"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid=".edy4-0"
                >
                  Flatscreen TV
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="gp9ht03"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="-3uz9.l"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid=".pnkztj"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="-lbu8s:"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="nv_l9l5"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="ryfvny-"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="881wz8q"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Heritage Single Section"
                data-oid="7qh:p.z"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="159p_ff"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Heritage Single Rooms"
                data-oid=":v5f2f7"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="--spy6_" />
                {t("common.share")}
              </Button>
            </div>
          </div>

          {/* Right Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl order-1 lg:order-2"
            data-oid="jr5n.61"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="egjbwm7"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="vi:5p6."
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="u7a5yj:"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="w0ort-u"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="ge:3-9:" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="in:5sym"
                >
                  <ChevronRight className="w-5 h-5" data-oid="j83747." />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="aqoxnu3"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="nyw7z_5"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeritageSingleSection;
